/**
 * CurrencyInput Component
 *
 * Reusable input component for Canadian dollar (CAD) currency amounts
 *
 * @component
 * @example
 * ```tsx
 * <CurrencyInput
 *   name="rrspBalance"
 *   value={50000}
 *   onChange={(field, value) => handleChange(field, value)}
 *   onBlur={(field, value) => handleBlur(field, value)}
 *   label="RRSP Balance"
 *   placeholder="e.g., 50000"
 *   error={errors.rrspBalance}
 * />
 * ```
 */

import React, { useState } from "react";
import { cn } from "../utils/cn";
import { renderHelpText } from "../utils/helpTextRenderer";
import { formatNumber } from "../utils/formatting";

export interface CurrencyInputProps {
  /** Field name for form handling */
  name: string;
  /** Current numeric value */
  value?: number | null;
  /** Callback when value changes (optimistic update) */
  onChange: (name: string, value: number | null) => void;
  /** Callback when input loses focus (triggers save) */
  onBlur: (name: string, value: number | null) => void;
  /** Label text displayed above input */
  label: string;
  /** Optional error message */
  error?: string;
  /** Optional help text displayed below input */
  helpText?: string;
  /** Optional suffix to display on the right side of input (e.g., "/mo") */
  suffix?: string;
  /** Optional placeholder text (defaults to "0") */
  placeholder?: string;
}

/**
 * Strip all non-numeric characters except digits, decimal point, and minus sign.
 */
function stripNonNumeric(raw: string): string {
  return raw.replace(/[^0-9.-]/g, "");
}

/**
 * Format a numeric value with thousand separators for display.
 * Returns empty string for null/undefined.
 */
function formatDisplayValue(value: number | null | undefined): string {
  if (value === null || value === undefined) return "";
  return formatNumber(value);
}

/**
 * CurrencyInput - Input field for CAD currency amounts
 *
 * Features:
 * - Formats with thousand separators when not focused (e.g., "50,000")
 * - Shows raw numeric value when focused for easy editing
 * - Validates non-negative numbers
 * - Converts empty input to 0
 * - Handles decimal values
 * - Displays error messages
 * - Accessible with proper labels
 *
 * @param props - Component props
 * @returns Currency input field
 */
const CurrencyInput: React.FC<CurrencyInputProps> = ({
  name,
  value,
  onChange,
  onBlur,
  label,
  error,
  helpText,
  suffix,
  placeholder = "0",
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [rawInput, setRawInput] = useState("");

  const handleFocus = () => {
    setIsFocused(true);
    setRawInput(value === null || value === undefined ? "" : String(value));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleaned = stripNonNumeric(e.target.value);
    setRawInput(cleaned);
    const numValue = cleaned === "" ? null : parseFloat(cleaned);
    if (numValue === null || !isNaN(numValue)) {
      onChange(name, numValue);
    }
  };

  const handleInputBlur = () => {
    const effectiveRaw = isFocused ? rawInput : String(value ?? 0);
    setIsFocused(false);
    const numValue = effectiveRaw === "" ? null : parseFloat(effectiveRaw);
    const normalizedValue = numValue === null || isNaN(numValue) ? 0 : numValue;
    onBlur(name, normalizedValue);
  };

  const displayValue = isFocused ? rawInput : formatDisplayValue(value);

  const currencyId = `${name}-currency`;
  const helpId = helpText ? `${name}-help` : undefined;
  const describedByIds = [currencyId, helpId].filter(Boolean).join(" ");

  return (
    <div>
      <label htmlFor={name} className="form-label">
        {label}
      </label>
      <div className="form-input-wrapper">
        <span
          className="currency-input-prefix"
          id={currencyId}
          aria-hidden="true"
        >
          $
        </span>
        <input
          type="text"
          inputMode="decimal"
          id={name}
          name={name}
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleInputBlur}
          onWheel={(e) => e.currentTarget.blur()}
          className={cn(
            "form-input form-input-with-prefix",
            suffix && "form-input-with-suffix"
          )}
          placeholder={placeholder}
          aria-describedby={describedByIds}
        />
        {suffix && <span className="input-suffix-unit">{suffix}</span>}
      </div>
      {helpText && (
        <p id={`${name}-help`} className="form-help-text">
          {renderHelpText(helpText)}
        </p>
      )}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default CurrencyInput;
